(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const initializedRails = new WeakSet();
  const heroes = new Map();
  const animated = new WeakSet();
  const animations = new Set();
  const revealDelays = new WeakMap();
  let revealObserver;
  let headerFrame = 0;
  const easing = 'cubic-bezier(.22,.68,.22,1)';

  function reveal(element) {
    if (!element || animated.has(element) || reduced.matches || window.Shopify?.designMode || !element.animate) return;
    animated.add(element);
    const distance = window.innerWidth < 750 ? 12 : 20;
    const animation = element.animate([
      {transform: `translateY(${distance}px)`, opacity: .35},
      {transform: 'translateY(0)', opacity: 1}
    ], {duration: 620, easing, delay: revealDelays.get(element) || 0});
    animations.add(animation);
    animation.finished.catch(() => {}).then(() => animations.delete(animation));
  }

  function initializeHero(element) {
    if (heroes.has(element)) return;
    const scenes = [...element.querySelectorAll('[data-sp-scene]')];
    const controls = element.querySelector('[data-sp-hero-controls]');
    const buttons = [...element.querySelectorAll('[data-sp-scene-button]')];
    const pause = element.querySelector('[data-sp-hero-pause]');
    const name = element.querySelector('[data-sp-scene-name]');
    const status = element.querySelector('[data-sp-scene-status]');
    if (scenes.length < 2 || !controls || !pause) return;
    const abort = new AbortController();
    const options = {signal: abort.signal};
    let index = 0;
    let timer;
    let request = 0;
    let manuallyPaused = false;
    let hovering = false;
    let focusing = false;
    let visible = true;
    let pointerStart;
    let observer;
    const interval = Math.max(4000, Number(element.dataset.interval) || 4800);
    const enabled = element.dataset.autoplay !== 'false' && !window.Shopify?.designMode;
    const shouldRun = () => enabled && !reduced.matches && !manuallyPaused && !hovering && !focusing && visible && !document.hidden;

    async function loadScene(nextIndex) {
      const image = scenes[nextIndex].querySelector('img');
      if (!image) return false;
      if (!image.getAttribute('src')) {
        if (image.dataset.srcset) image.setAttribute('srcset', image.dataset.srcset);
        image.setAttribute('src', image.dataset.src);
      }
      try {
        await image.decode();
        return image.naturalWidth > 0;
      } catch {
        return image.complete && image.naturalWidth > 0;
      }
    }

    function sync() {
      clearTimeout(timer);
      const running = shouldRun();
      element.classList.toggle('is-running', running);
      element.classList.toggle('is-paused', manuallyPaused || reduced.matches || !enabled);
      pause.disabled = reduced.matches || !enabled;
      pause.setAttribute('aria-label', element.classList.contains('is-paused') ? element.dataset.playLabel : element.dataset.pauseLabel);
      if (running) timer = setTimeout(() => show((index + 1) % scenes.length, false), interval);
    }

    async function show(nextIndex, userInitiated = true) {
      clearTimeout(timer);
      const token = ++request;
      const loaded = await loadScene(nextIndex);
      if (abort.signal.aborted || token !== request) return;
      if (!userInitiated && !shouldRun()) { sync(); return; }
      if (loaded) {
        index = nextIndex;
        scenes.forEach((scene, i) => {
          scene.classList.toggle('is-active', i === index);
          scene.setAttribute('aria-hidden', String(i !== index));
          buttons[i]?.setAttribute('aria-pressed', String(i === index));
        });
        name.textContent = scenes[index].dataset.name;
        status.setAttribute('aria-live', userInitiated ? 'polite' : 'off');
        status.textContent = String(index + 1) + ' / ' + scenes.length + ' — ' + scenes[index].dataset.name;
        loadScene((index + 1) % scenes.length);
      }
      sync();
    }

    buttons.forEach((button, i) => button.addEventListener('click', () => show(i), options));
    pause.addEventListener('click', () => { manuallyPaused = !manuallyPaused; sync(); }, options);
    element.addEventListener('pointerenter', event => {
      if (event.pointerType !== 'mouse') return;
      hovering = true;
      sync();
    }, options);
    element.addEventListener('pointerleave', () => { hovering = false; sync(); }, options);
    element.addEventListener('focusin', () => { focusing = true; sync(); }, options);
    element.addEventListener('focusout', event => { focusing = element.contains(event.relatedTarget); sync(); }, options);
    element.addEventListener('keydown', event => {
      if (!event.target.closest('[data-sp-scene-button]') || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      const next = (index + (event.key === 'ArrowRight' ? 1 : -1) + scenes.length) % scenes.length;
      buttons[next]?.focus();
      show(next);
    }, options);
    element.addEventListener('pointerdown', event => {
      if (event.pointerType === 'mouse' || event.target.closest('button, a')) return;
      pointerStart = {x: event.clientX, y: event.clientY};
    }, options);
    element.addEventListener('pointerup', event => {
      if (!pointerStart) return;
      const dx = event.clientX - pointerStart.x;
      const dy = event.clientY - pointerStart.y;
      pointerStart = null;
      if (Math.abs(dx) > 45 && Math.abs(dy) < 35) show((index + (dx < 0 ? 1 : -1) + scenes.length) % scenes.length);
    }, options);
    element.addEventListener('pointercancel', () => { pointerStart = null; }, options);
    document.addEventListener('visibilitychange', sync, options);
    reduced.addEventListener('change', sync, options);
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(entries => { visible = entries[0].isIntersecting; sync(); }, {threshold: .1});
      observer.observe(element);
    }
    controls.hidden = false;
    element.style.setProperty('--sp-scene-duration', String(interval) + 'ms');
    sync();
    // Secondary scenes have no initial src: prepare only the next scene after LCP.
    const first = scenes[0].querySelector('img');
    first?.decode().catch(() => {}).then(() => {
      if (!abort.signal.aborted) loadScene(1);
    });
    heroes.set(element, () => { clearTimeout(timer); ++request; abort.abort(); observer?.disconnect(); });
  }

  function initialize(root = document) {
    root.querySelectorAll('[data-sp-hero]').forEach(initializeHero);
    root.querySelectorAll('[data-sp-product-rail]').forEach(section => {
      if (initializedRails.has(section)) return;
      const rail = section.querySelector('.sp-product-grid');
      const controls = section.querySelector('.sp-home-rail-controls');
      if (!rail || !controls || !rail.children.length) return;
      initializedRails.add(section);
      const previous = controls.querySelector('[data-sp-prev]');
      const next = controls.querySelector('[data-sp-next]');
      const status = controls.querySelector('[data-sp-rail-status]');
      const cardStep = () => rail.children[0].getBoundingClientRect().width + (parseFloat(getComputedStyle(rail).columnGap) || 0);
      const update = () => {
        const index = Math.round(rail.scrollLeft / cardStep());
        previous.disabled = rail.scrollLeft < 2;
        next.disabled = rail.scrollLeft >= rail.scrollWidth - rail.clientWidth - 2;
        const position = Math.min(index + 1, rail.children.length);
        status.textContent = String(position).padStart(2, '0') + ' / ' + String(rail.children.length).padStart(2, '0');
        status.style.setProperty('--sp-rail-progress', String(position / rail.children.length * 100) + '%');
      };
      previous.addEventListener('click', () => rail.scrollBy({left: -cardStep(), behavior: reduced.matches ? 'instant' : 'smooth'}));
      next.addEventListener('click', () => rail.scrollBy({left: cardStep(), behavior: reduced.matches ? 'instant' : 'smooth'}));
      rail.addEventListener('scroll', update, {passive: true});
      if ('ResizeObserver' in window) new ResizeObserver(update).observe(rail);
      controls.hidden = false;
      update();
    });
    if (reduced.matches || window.Shopify?.designMode || !('IntersectionObserver' in window)) return;
    if (!revealObserver) {
      revealObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          reveal(entry.target);
          revealObserver.unobserve(entry.target);
        });
      }, {threshold: .12, rootMargin: '0px 0px -20px 0px'});
    }
    root.querySelectorAll('[data-sp-reveal], .sp-home-heading, .sp-discover__link, .sp-campaign-teasers__header, .sp-campaign-teaser__body, .sp-home-featured .sp-section-heading, .sp-product-card__details, .sp-home-stock__copy, .sp-home-stock__business, .sp-editorial__header, .sp-payment-trust__header, .collection-hero__text-wrapper, .product__info-container, .sp-page__header, .sp-campaign-page__hero-copy, .sp-campaign-detail__content').forEach(element => {
      if (element.parentElement?.closest('[data-sp-reveal], .product__info-container')) return;
      if (element.matches('.sp-discover__link, .sp-campaign-teaser__body, .sp-product-card__details')) {
        const siblings = element.matches('.sp-discover__link') ? [...element.parentElement.children] : [...element.parentElement.parentElement.children];
        const sibling = element.matches('.sp-discover__link') ? element : element.parentElement;
        revealDelays.set(element, Math.min(Math.max(0, siblings.indexOf(sibling)), 3) * 55);
      }
      if (!animated.has(element)) revealObserver.observe(element);
    });
  }
  function updateHeader() {
    headerFrame = 0;
    document.querySelector('.sp-header')?.classList.toggle('is-scrolled', window.scrollY > 24);
  }
  window.addEventListener('scroll', () => {
    if (!headerFrame) headerFrame = requestAnimationFrame(updateHeader);
  }, {passive: true});
  document.addEventListener('click', event => {
    const link = event.target.closest('.sp-header__mobile-menu a');
    if (link) link.closest('details').open = false;
  });
  reduced.addEventListener('change', () => {
    if (reduced.matches) {
      animations.forEach(animation => animation.cancel());
      animations.clear();
      revealObserver?.disconnect();
    } else initialize();
  });
  initialize();
  updateHeader();
  document.addEventListener('shopify:section:load', event => { initialize(event.target); updateHeader(); });
  document.addEventListener('shopify:section:unload', event => {
    heroes.forEach((destroy, element) => {
      if (!event.target.contains(element)) return;
      destroy();
      heroes.delete(element);
    });
  });
})();
